import React, { useEffect, useMemo, useState } from "react";
import HostNav from "../components/hostComponents/HostNav";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";

import { CircularProgress } from "@mui/material";
import { getFlags, getHostProperties, getHostEarnings, getAllOrders } from "../store/thunks/hostActions";
import { buildPropertyEarningsMap } from "../utils/earningsAggregation";

const EARNED_STATUSES = new Set(["completed", "paymentcompleted"]);

function isEarnedOrder(order) {
  return EARNED_STATUSES.has(String(order?.status || "").toLowerCase());
}

function orderEarnedDate(order) {
  const raw = order?.inStockDate || order?.createdAt;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

function MetricCard({ label, value, subtitle, onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      className={`text-left rounded-[14px] bg-white border border-[#EAEAEA] px-4 py-3.5 transition-colors ${
        onClick ? "hover:border-[#111111] cursor-pointer" : ""
      }`}
    >
      <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#888888]">{label}</p>
      <p className="mt-1 text-[20px] font-extrabold leading-tight tabular-nums text-[#111111]" style={{ letterSpacing: -0.3 }}>
        {value}
      </p>
      {subtitle && <p className="mt-0.5 text-[11px] text-[#888888]">{subtitle}</p>}
    </Tag>
  );
}

function TopEarningProperties({ propertyEarningsMap, properties }) {
  const navigate = useNavigate();

  const propertyEarningsData = properties
    ?.filter((p) => !p.isDeleted)
    ?.map((property) => {
      const totals = propertyEarningsMap?.[property._id] || {};
      return {
        ...property,
        totalEarnings: totals.totalHostFee || 0,
        orderCount: totals.orderCount || 0,
      };
    })
    ?.filter((p) => p.totalEarnings > 0)
    ?.sort((a, b) => b.totalEarnings - a.totalEarnings)
    ?.slice(0, 6);

  return (
    <div className="rounded-[18px] bg-white border border-[#EAEAEA] p-5">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#888888] mb-0.5">Performance</p>
          <h2 className="text-[16px] font-extrabold text-[#111111]">Top Earning Properties</h2>
        </div>
        <Link
          to="/properties"
          className="inline-flex items-center gap-1 h-8 px-3 rounded-[10px] border border-[#EAEAEA] bg-white text-[12px] font-bold text-[#5F6368] transition hover:text-[#111111] hover:border-[#111111]"
        >
          View All
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>

      {!propertyEarningsData || propertyEarningsData.length === 0 ? (
        <div className="rounded-[14px] bg-[#FAFAFA] px-6 py-10 text-center">
          <h3 className="text-[14px] font-extrabold text-[#111111] mb-1">No Earnings Yet</h3>
          <p className="text-[12px] text-[#888888]">Property earnings will appear here once orders are completed</p>
        </div>
      ) : (
        <div className="divide-y divide-[#F0F0F0]">
          {propertyEarningsData.map((property, index) => (
            <button
              key={property._id}
              type="button"
              onClick={() => navigate(`/properties/${property._id}`)}
              className="w-full flex items-center gap-3 px-2 py-3 text-left rounded-[10px] transition-colors hover:bg-[#FCFAF7]"
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#F5F5F5] text-[11px] font-extrabold text-[#5F6368]">
                {index + 1}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-bold text-[#111111] truncate">
                  {property.listingTitle || `${property.address?.city || "Untitled"} Property`}
                </span>
                <span className="block text-[11px] text-[#888888] truncate">
                  {[property.address?.line1, property.address?.city].filter(Boolean).join(", ")}
                </span>
              </span>
              <span className="text-right shrink-0">
                <span className="block text-[13px] font-extrabold text-[#047857] tabular-nums">
                  ${property.totalEarnings.toFixed(2)}
                </span>
                <span className="block text-[11px] text-[#888888]">
                  {property.orderCount} order{property.orderCount !== 1 ? "s" : ""}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EarningsChart({ orders }) {
  const monthlyData = useMemo(() => {
    const months = Array.from({ length: 6 }, (_, i) => {
      const date = new Date();
      date.setDate(1);
      date.setMonth(date.getMonth() - (5 - i));
      return {
        key: `${date.getFullYear()}-${date.getMonth()}`,
        month: date.toLocaleDateString("en-US", { month: "short" }),
        amount: 0,
      };
    });
    const byKey = new Map(months.map((m) => [m.key, m]));

    (orders || []).forEach((order) => {
      if (!isEarnedOrder(order)) return;
      const date = orderEarnedDate(order);
      if (!date) return;
      const bucket = byKey.get(`${date.getFullYear()}-${date.getMonth()}`);
      if (bucket) bucket.amount += Number(order.hostFee) || 0;
    });

    return months;
  }, [orders]);

  const maxAmount = Math.max(...monthlyData.map((d) => d.amount), 1);
  const hasData = monthlyData.some((d) => d.amount > 0);

  return (
    <div className="rounded-[18px] bg-white border border-[#EAEAEA] p-5">
      <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#888888] mb-0.5">Trend</p>
      <h3 className="text-[16px] font-extrabold text-[#111111] mb-4">Earnings by Month</h3>

      {hasData ? (
        <div className="flex items-end justify-between h-28 gap-2">
          {monthlyData.map((data) => {
            const height = Math.max((data.amount / maxAmount) * 100, data.amount > 0 ? 4 : 2);
            return (
              <div key={data.key} className="flex flex-col items-center flex-1 min-w-0" title={`$${data.amount.toFixed(2)}`}>
                <div
                  className={`w-full rounded-t-[6px] transition-colors ${data.amount > 0 ? "bg-[#111111] hover:bg-[#333333]" : "bg-[#EAEAEA]"}`}
                  style={{ height: `${height}%` }}
                />
                <span className="mt-2 text-[10px] font-bold text-[#888888]">{data.month}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-[12px] text-[#888888] py-8 text-center">No completed orders in the last 6 months</p>
      )}
    </div>
  );
}

function QuickActionsPanel({ hostInfo }) {
  const navigate = useNavigate();

  const actions = [
    { title: "Add Property", icon: "M12 6v6m0 0v6m0-6h6m-6 0H6", link: "/add-property" },
    { title: "View Orders", icon: "M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z", link: "/order-list" },
    { title: "Properties", icon: "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6", link: "/properties" },
    { title: "Settings", icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z", link: `/settings/${hostInfo?._id ?? ""}` },
  ];

  return (
    <div className="rounded-[18px] bg-white border border-[#EAEAEA] p-5">
      <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#888888] mb-3">Quick Actions</p>
      <div className="grid grid-cols-2 gap-2">
        {actions.map((action) => (
          <button
            key={action.title}
            onClick={() => navigate(action.link)}
            className="flex items-center gap-2 rounded-[12px] border border-[#EAEAEA] bg-white px-3 py-2.5 text-left transition-colors hover:border-[#111111]"
          >
            <svg className="w-4 h-4 shrink-0 text-[#5F6368]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={action.icon} />
            </svg>
            <span className="text-[12px] font-bold text-[#111111]">{action.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function EarningsDashboard({ hostInfo }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { properties, loading: propertiesLoading } = useSelector((state) => state.hostProperties);
  const { earnings, loading: earningsLoading } = useSelector((state) => state.hostEarnings);
  const { orders: allOrders, loading: allOrdersLoading } = useSelector((state) => state.orders);
  const [loading, setLoading] = useState(true);

  const propertyEarningsMap = buildPropertyEarningsMap({
    orders: allOrders,
    earningsByProperty: earnings,
  });

  const { totalEarnings, totalOrders, avgOrderValue, monthToDateEarnings } = useMemo(() => {
    let total = 0;
    let count = 0;

    if (propertyEarningsMap && typeof propertyEarningsMap === "object") {
      for (const propertyId in propertyEarningsMap) {
        const summary = propertyEarningsMap[propertyId] || {};
        total += summary.totalHostFee || 0;
        count += summary.orderCount || 0;
      }
    }

    const now = new Date();
    const mtd = (allOrders || []).reduce((sum, order) => {
      if (!isEarnedOrder(order)) return sum;
      const date = orderEarnedDate(order);
      if (!date || date.getFullYear() !== now.getFullYear() || date.getMonth() !== now.getMonth()) return sum;
      return sum + (Number(order.hostFee) || 0);
    }, 0);

    return {
      totalEarnings: total,
      totalOrders: count,
      avgOrderValue: count > 0 ? total / count : 0,
      monthToDateEarnings: mtd,
    };
  }, [propertyEarningsMap, allOrders]);

  useEffect(() => {
    if (hostInfo?._id) {
      dispatch(getHostProperties(hostInfo._id));
      dispatch(getHostEarnings(hostInfo._id));
      dispatch(getAllOrders(hostInfo._id));
      setLoading(false);
    }
  }, [dispatch, hostInfo]);

  if (loading || propertiesLoading || earningsLoading || allOrdersLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <CircularProgress size={60} sx={{ color: "#111111" }} />
          <p className="mt-4 text-[13px] text-[#888888] font-semibold">Loading earnings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] overflow-auto">
      <div className="max-w-[1200px] mx-auto px-6 py-6">
        <div className="mb-5 flex items-end justify-between gap-3">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888] mb-1">Earnings</p>
            <h1 className="text-[26px] font-extrabold text-[#111111] leading-tight" style={{ letterSpacing: -0.5 }}>
              Earnings Overview
            </h1>
            <p className="text-[13px] text-[#5F6368] mt-0.5">Commission performance across your properties</p>
          </div>
          <Link
            to="/host"
            className="inline-flex shrink-0 items-center gap-1.5 h-9 px-3 rounded-[10px] border border-[#EAEAEA] bg-white text-[12px] font-bold text-[#5F6368] transition hover:text-[#111111] hover:border-[#111111]"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Dashboard
          </Link>
        </div>

        <div className="grid grid-cols-2 laptop:grid-cols-4 gap-2.5 mb-4">
          <MetricCard label="Total Earnings" value={`$${totalEarnings.toFixed(2)}`} subtitle="All time" onClick={() => navigate("/order-list")} />
          <MetricCard label="This Month" value={`$${monthToDateEarnings.toFixed(2)}`} subtitle="Month to date" />
          <MetricCard label="Total Orders" value={String(totalOrders)} subtitle="Completed" onClick={() => navigate("/order-list")} />
          <MetricCard label="Avg Order Value" value={`$${avgOrderValue.toFixed(2)}`} subtitle="Per order" />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-2.5">
          <div className="xl:col-span-2">
            <TopEarningProperties propertyEarningsMap={propertyEarningsMap} properties={properties} />
          </div>
          <div className="space-y-2.5">
            <EarningsChart orders={allOrders} />
            <QuickActionsPanel hostInfo={hostInfo} />
          </div>
        </div>
      </div>
    </div>
  );
}

function HostDashboard() {
  const dispatch = useDispatch();
  const { hostInfo } = useSelector((state) => state.host);
  const isOnBoarded = useSelector((state) => state.hostDetails.Info?.isOnBoarded);

  useEffect(() => {
    if (hostInfo?._id) dispatch(getFlags(hostInfo._id));
  }, [dispatch, hostInfo?._id]);

  return (
    <div className="flex h-screen w-full overflow-auto bg-[#FAFAFA]">
      <div className="mx-auto flex w-full flex-col px-6 tablet:px-0">
        <HostNav dashboard={true} hideproperty={true} />
        {isOnBoarded ? (
          <EarningsDashboard hostInfo={hostInfo} />
        ) : (
          <div className="flex items-center justify-center py-16 px-4">
            <div className="w-full max-w-md text-center rounded-[18px] bg-white border border-[#EAEAEA] p-8">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-[16px] bg-[#111111]">
                <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>

              <h3 className="text-[20px] font-extrabold text-[#111111] mb-2">Connect Stripe to Start Earning</h3>
              <p className="text-[13px] text-[#5F6368] mb-6 max-w-sm mx-auto">
                Link your Stripe account when you are ready to receive host payouts from Bellhopt orders.
              </p>

              <Link
                to="/onboarding"
                className="block w-full rounded-[12px] bg-[#111111] px-6 py-3.5 text-[14px] font-bold text-white transition hover:bg-black"
              >
                Continue setup
              </Link>
              <p className="mt-3 text-[11px] text-[#999999]">Setup will resume at the next relevant activation step.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default HostDashboard;
